import { uniform, float, vec4, positionWorld, varying, step, screenCoordinate } from 'three/tsl';

/**
 * @file `createOcclusionFade` — "I cannot see my character, the building is in
 * the way". A periodic ray cast from the camera to the follow target, and
 * whatever it passes through is dissolved until it is not in the way any more.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY VOLUMES AND NOT MESHES
 * ─────────────────────────────────────────────────────────────────────────────
 * There is no per-building mesh to turn transparent. Static level geometry is
 * merged per MATERIAL and then chunked 3x3 (`world/Chunker.js`), so one draw
 * call holds a third of the city and one building is a few thousand vertices in
 * the middle of it. Tagging every vertex with a building id would mean a new
 * attribute through `GeoBuilder` -> `mergeGeo` -> the bake -> the chunker, on
 * every level, forever.
 *
 * The cheaper contract: a fader owns a handful of WORLD-SPACE BOXES, and the
 * shader asks "is this vertex inside a fading box?" from `positionWorld`. It
 * costs one vertex-stage varying and about ten instructions per slot, nothing
 * upstream of the material changes at all, and it works identically for merged
 * chunks (identity transform) and for `InstancedBatch` rooftop clutter (the
 * instance matrix is already folded into `positionWorld`).
 *
 * The price is that the fade region is a BOX, not a silhouette: a neighbour's
 * wall inside the same box fades with it. That is why {@link FadeVolume} has a
 * soft `margin` — the weight ramps to 0 at the box face, so anything grazing
 * the boundary barely moves — and why the city grows its boxes by less than the
 * gap between two lots.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A SCREEN DOOR AND NOT ALPHA
 * ─────────────────────────────────────────────────────────────────────────────
 * Real blending is the wrong tool three times over here:
 *   1. the material is shared by a whole chunk, so `transparent = true` would
 *      move a THIRD OF THE CITY into the sorted transparent queue — and this
 *      project has already been bitten by r185 sorting coplanar layers by
 *      bounding-sphere centre (see the decal note in `levels/city/index.js`);
 *   2. a building is a closed box, so its own far wall blends through its near
 *      wall and the "ghost" reads as a solid lump of mush;
 *   3. `depthWrite: false` would let every prop behind it draw on top.
 *
 * So the fade is an ORDERED DITHER: a 4x4 Bayer threshold in SCREEN space,
 * compared against the fade amount, discarded through `alphaTest`. The material
 * stays opaque, depth stays correct, sorting never enters the picture, and the
 * pattern is pinned to the screen rather than to the surface so it does not
 * crawl as the camera orbits. At `strength` 0.7 roughly a third of the pixels
 * survive, which is enough to keep the silhouette legible while the player
 * reads clearly through it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHEN THE CAST IS THE WRONG QUESTION — `setForced`
 * ─────────────────────────────────────────────────────────────────────────────
 * The ray answers "is this box between the camera and the player", and there is
 * one case where that is not the question at all: the player is INSIDE the box.
 * A shop interior is an open-topped room with 3.4 m walls, and at the game's
 * 52 degrees of pitch the camera ray comes down over the top of the near wall
 * and never touches it — so the wall stays solid and its inner face hides the
 * counter and the shelving standing against it. From roughly half of all yaws
 * the room reads as an empty box; orbit 180 degrees and the furniture appears.
 *
 * No ray fixes that, because the wall genuinely is not on the ray. So the room
 * asks for it directly: {@link setForced} takes a handful of volume ids that are
 * treated as hit on every cast, whatever the geometry says. Everything
 * downstream is unchanged — they go through the same {@link assign}, take the
 * same sticky slots and run the same ramps, so a forced wall dissolves and
 * re-solidifies exactly like a building does.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * USING IT
 * ─────────────────────────────────────────────────────────────────────────────
 * ```js
 * const fade = createOcclusionFade({ slots: 6, strength: 0.7 });
 * fade.setVolumes(buildings.map(toFadeVolume));      // once, at build time
 * material = bakedMaterial({ map, fade });           // every material that can hide the player
 * // ...then, per frame, from the level's update hook:
 * fade.update(dt, engine.camera.position, engine.target);
 * ```
 * A material built with `fade` costs nothing while nothing is fading: every
 * slot's amount is 0, the varying is 0, and `step(0, bayer)` keeps every pixel.
 *
 * @module engine/render/OcclusionFade
 */

/** @typedef {import('three/tsl').ShaderNodeObject<any>} TSLNode */
/** @typedef {{x: number, y: number, z: number}} Vec3Like */

/**
 * An axis-aligned world box that may be faded, plus the sub-boxes the ray is
 * actually tested against.
 *
 * Splitting the two matters for anything with a setback: a tower's SHADER box
 * has to cover every vertex it owns (so the fade does not slice through it
 * mid-storey), while its RAY boxes should hug the real tiers, or the empty air
 * above a podium counts as an occluder and the tower fades every time the
 * player walks past it.
 *
 * @typedef {Object} FadeVolume
 * @property {string|number} id Stable across frames — it is the slot key.
 * @property {number} minX @property {number} minY @property {number} minZ
 * @property {number} maxX @property {number} maxY @property {number} maxZ
 * @property {Array<{minX: number, minY: number, minZ: number, maxX: number, maxY: number, maxZ: number}>} [parts]
 *   Ray-test boxes. Defaults to the volume itself.
 */

/** @typedef {{center: TSLNode, half: TSLNode, id: string|number|null, amount: number, want: number}} FadeSlot */

/** Where an unused slot's box is parked. Belt and braces — `amount` is 0 too. */
const PARKED = 1e7;

/**
 * The offsets, in metres, of the extra ray endpoints around the follow target.
 * `[right, up]` in a camera-relative basis.
 *
 * ONE ray down the middle is not enough: it threads a doorway or the gap
 * between two towers and reports "clear" while the player is plainly behind a
 * wall. Four rays outline the character's screen box (chest, head, both
 * shoulders), which is what "can I see them" actually means.
 */
const RAY_OFFSETS = /** @type {const} */ ([
  [0, 0],
  [0, 0.95],
  [0.55, 0.35],
  [-0.55, 0.35],
]);

/**
 * Ceiling on the forced set, and it is a SLOT-PRESSURE guard, not a taste
 * number. Forced ids are spliced in at the FRONT of the hit list, i.e. they win
 * slots over every real occluder, so an unbounded set could starve the building
 * fades entirely.
 *
 * Four is what the only caller needs (a room has four walls and at most two of
 * them ever face the camera), and against the city's `slots: 6` it leaves two
 * for genuine occluders even in the worst case. In practice the pressure is
 * nil: the only thing that forces anything is a player standing in a shop
 * interior, and those rooms sit 100 m under the island in an otherwise empty
 * slot region — there is no building between that camera and that player. The
 * cap exists so a future caller cannot silently break the outdoor fade.
 */
const FORCED_MAX = 4;

/**
 * @param {Object} [o]
 * @param {number} [o.slots=6] Maximum buildings dissolving AT ONCE. Each slot is
 *   two vec4 uniforms and ~10 vertex instructions on every material built with
 *   this fader, so this is the real cost knob. Six covers a camera looking
 *   along an avenue at 52 degrees of pitch; the nearest occluders win when more
 *   than `slots` are hit.
 * @param {number} [o.strength=0.7] Dither cut at full fade: 0 = solid,
 *   1 = invisible. 0.7 keeps ~30% of the pixels, which reads as a ghost that
 *   still has a silhouette.
 * @param {number} [o.interval=0.1] Seconds between ray casts. The RAMP runs
 *   every frame regardless — this only sets how often the SET changes, and
 *   10 Hz is well under the ~0.15 s the ramp takes anyway.
 * @param {number} [o.fadeIn=0.14] Seconds 0 -> 1 once a volume is hit.
 * @param {number} [o.fadeOut=0.3] Seconds 1 -> 0 once it is clear. Deliberately
 *   slower: a building flickering back to solid as the player edges past a
 *   corner is worse than one that lingers.
 * @param {number} [o.margin=0.6] Metres of soft edge inside each box face.
 * @param {number} [o.maxDistance=0] Ignore volumes whose entry point is further
 *   than this from the camera. 0 = no limit.
 * @returns {Object} The fader — see the module header.
 */
export function createOcclusionFade(o = {}) {
  const {
    slots: slotCount = 6,
    strength = 0.7,
    interval = 0.1,
    fadeIn = 0.14,
    fadeOut = 0.3,
    margin = 0.6,
    maxDistance = 0,
  } = o;

  const n = Math.max(1, slotCount | 0);
  const inv = -1 / Math.max(1e-3, margin);

  /** @type {FadeSlot[]} */
  const slots = [];
  for (let i = 0; i < n; i++) {
    slots.push({
      // xyz = box centre, w = fade amount (0..1).
      center: uniform(vec4(PARKED, PARKED, PARKED, 0)),
      // xyz = half extents, w = -1/margin (folded in so the shader has no divide).
      half: uniform(vec4(1, 1, 1, inv)),
      id: null,
      amount: 0,
      want: 0,
    });
  }

  /** @type {FadeVolume[]} */
  let volumes = [];
  /** @type {Map<string|number, FadeVolume>} */
  let byId = new Map();

  let acc = interval; // cast on the very first update, not `interval` later
  let casts = 0;
  /** @type {TSLNode|null} */
  let fadeVar = null;
  /** @type {TSLNode|null} */
  let cutNode = null;
  /** @type {TSLNode|null} */
  let factorNode = null;

  /** @type {Array<{id: string|number, t: number}>} */
  const hits = [];

  // ── forcing (see the module header) ────────────────────────────────────────

  /** Ids treated as hit on every cast. Empty = the fader behaves exactly as it
   *  did before this existed: nothing below runs at all. @type {Array<string|number>} */
  let forcedIds = [];
  /** Reused hit records for them — a per-step machine must not litter, and this
   *  runs at the cast rate. `cast` clears `hits` before each fill, so an entry
   *  can never be in the list twice over. @type {Array<{id: string|number, t: number}>} */
  const forcedHits = [];

  /**
   * Splices the forced ids into the FRONT of a freshly cast hit list, so they
   * are the "nearest" and take slots first. A forced id the ray also found is
   * de-duplicated (it would otherwise be considered twice and could spend two
   * slots on one volume); one that names nothing in the current volume set is
   * dropped by {@link assign} on its own.
   */
  const injectForced = () => {
    for (let i = forcedIds.length - 1; i >= 0; i--) {
      const id = forcedIds[i];
      if (!byId.has(id)) continue;
      for (let j = hits.length - 1; j >= 0; j--) if (hits[j].id === id) hits.splice(j, 1);
      let e = forcedHits[i];
      if (!e) {
        e = { id, t: -1 };
        forcedHits[i] = e;
      }
      e.id = id;
      e.t = -1;
      hits.unshift(e);
    }
  };

  /**
   * @param {Array<string|number>|null} [ids] Up to {@link FORCED_MAX}; null or
   *   empty clears.
   * @returns {number} How many ids are forced now.
   */
  const setForced = (ids) => {
    const next = Array.isArray(ids) ? ids.slice(0, FORCED_MAX) : [];
    if (next.length === forcedIds.length && next.every((id, i) => forcedIds[i] === id)) {
      return forcedIds.length;
    }
    forcedIds = next;
    // Re-cast on the very NEXT update rather than waiting out `interval`: what
    // changed is the caller's answer, not the geometry, and a room's near wall
    // that stays solid for another 100 ms is the bug this exists to fix.
    acc = interval;
    return forcedIds.length;
  };

  // ── the node graph (built once, on first use) ──────────────────────────────

  /**
   * Per-vertex fade weight: `max over slots of amount * insideness`.
   * Computed in the VERTEX stage and interpolated — see the project rule about
   * per-instance values decoded in the fragment stage. Here it is not even a
   * correctness issue (the value is smooth), it is a cost one: this is `slots`
   * box tests, and paying them per pixel would be absurd.
   * @returns {TSLNode} float 0..1
   */
  const fadeNode = () => {
    if (fadeVar) return fadeVar;
    let accNode = float(0);
    for (const s of slots) {
      // Signed distance to the box, per axis: <= 0 is inside.
      const d = positionWorld.sub(s.center.xyz).abs().sub(s.half.xyz);
      const outside = d.x.max(d.y).max(d.z);
      // `half.w` is -1/margin, so this is clamp((-outside)/margin, 0, 1):
      // 1 well inside the box, ramping to 0 at the face.
      const w = outside.mul(s.half.w).clamp(0, 1);
      accNode = accNode.max(w.mul(s.center.w));
    }
    fadeVar = varying(accNode, 'vOccFade');
    return fadeVar;
  };

  return {
    /** @type {number} */
    strength,
    /** The `alphaTest` a screen-doored material must carry. */
    alphaTest: 0.5,

    /**
     * Replaces the candidate set. Call once, after the level's geometry exists.
     * @param {FadeVolume[]} list Kept by reference — do not mutate it after.
     * @returns {number} How many volumes are now live.
     */
    setVolumes(list) {
      volumes = Array.isArray(list) ? list : [];
      byId = new Map();
      for (const v of volumes) byId.set(v.id, v);
      // A forced id names a volume in the OLD set; carrying it across would
      // either dangle or, worse, match something unrelated in the new one.
      forcedIds = [];
      for (const s of slots) {
        s.id = null;
        s.amount = 0;
        s.want = 0;
        s.center.value.set(PARKED, PARKED, PARKED, 0);
      }
      return volumes.length;
    },

    /**
     * 0/1 opacity for an OPAQUE material: the screen-door cut. Multiply it into
     * whatever opacity the material already has and set `alphaTest = 0.5`
     * ({@link bakedMaterial} does both for you).
     * @returns {TSLNode} float, 1 = keep this pixel, 0 = discard it.
     */
    opacity() {
      if (!cutNode) cutNode = step(fadeNode().mul(float(strength)), bayer4(screenCoordinate.xy));
      return cutNode;
    },

    /**
     * Smooth `1 - fade * strength`, for a material that is ALREADY blended and
     * can just get dimmer — the city's emissive window shells. Do not use this
     * on opaque geometry; that is what {@link opacity} is for.
     * @returns {TSLNode} float 0..1
     */
    factor() {
      if (!factorNode) factorNode = float(1).sub(fadeNode().mul(float(strength)));
      return factorNode;
    },

    /**
     * One step. Ramps every slot, and re-casts at most every `interval`.
     *
     * @param {number} dt Seconds.
     * @param {Vec3Like} from Camera position.
     * @param {Vec3Like} to Follow target (`engine.target` — the point the camera
     *   is actually looking at, which is the character's chest).
     * @returns {number} How many volumes are fading (amount > 0).
     */
    update(dt, from, to) {
      if (!(dt > 0) || !from || !to) return 0;

      acc += dt;
      if (acc >= interval && volumes.length) {
        acc = 0;
        cast(volumes, from, to, hits, maxDistance);
        casts++;
        if (forcedIds.length) injectForced();
        assign(slots, hits, byId);
      }

      let live = 0;
      for (const s of slots) {
        const rate = s.want > s.amount ? dt / Math.max(1e-3, fadeIn) : -dt / Math.max(1e-3, fadeOut);
        const next = Math.max(0, Math.min(1, s.amount + rate));
        if (next !== s.amount) {
          s.amount = next;
          s.center.value.w = next;
        }
        if (s.amount > 0) live++;
        // A slot only frees up once it has finished fading OUT, so a building
        // that stops occluding mid-ramp cannot have its box yanked out from
        // under it by the next candidate.
        else if (s.want === 0 && s.id !== null) {
          s.id = null;
          s.center.value.set(PARKED, PARKED, PARKED, 0);
        }
      }
      return live;
    },

    /**
     * Dissolves these volumes whether or not anything is in the way — see the
     * module header. They are treated as the NEAREST hits, so they take slots
     * first, and everything after that is the ordinary path: the same sticky
     * assignment, the same `fadeIn`/`fadeOut` ramps. Dropping an id out of the
     * set is not a snap back to solid, it is a normal fade-out.
     *
     * Sticky in the other sense too: this is a STANDING instruction, not a
     * one-shot. It survives {@link reset} (which clears the slots, not the
     * policy — the ids re-assert themselves on the next cast) and is cleared by
     * {@link setVolumes} and {@link dispose}.
     *
     * @param {Array<string|number>|null} [ids] Up to {@link FORCED_MAX} volume
     *   ids; `null` or `[]` clears. COPIED, so a caller may hand over a scratch
     *   array it goes on mutating.
     * @returns {number} How many ids are forced now.
     */
    setForced(ids) {
      return setForced(ids);
    },

    /** Sugar for `setForced(null)`. @returns {number} Always 0. */
    clearForced() {
      return setForced(null);
    },

    /**
     * Clears every slot immediately (level teardown, a camera cut, a respawn).
     * NOT the forced set — see {@link setForced}.
     */
    reset() {
      acc = interval;
      for (const s of slots) {
        s.id = null;
        s.amount = 0;
        s.want = 0;
        s.center.value.set(PARKED, PARKED, PARKED, 0);
      }
    },

    /** @returns {{slots: number, volumes: number, active: number, casts: number, ids: Array<string|number>, amounts: number[], forced: Array<string|number>}} */
    stats() {
      /** @type {Array<string|number>} */
      const ids = [];
      /** @type {number[]} */
      const amounts = [];
      let active = 0;
      for (const s of slots) {
        if (s.amount > 0) active++;
        if (s.id !== null) {
          ids.push(s.id);
          amounts.push(s.amount);
        }
      }
      return { slots: n, volumes: volumes.length, active, casts, ids, amounts, forced: forcedIds.slice() };
    },

    /** Idempotent. The uniforms are plain node objects — nothing to free. */
    dispose() {
      volumes = [];
      byId = new Map();
      forcedIds = [];
      for (const s of slots) {
        s.id = null;
        s.amount = 0;
        s.want = 0;
        s.center.value.set(PARKED, PARKED, PARKED, 0);
      }
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// The cast
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fills `out` with every volume the camera->target segment passes through,
 * NEAREST FIRST.
 *
 * Four segments are traced (see {@link RAY_OFFSETS}) and a volume counts if any
 * of them hits it; the reported `t` is the closest entry across all four, which
 * is what the nearest-first ordering sorts on when there are more occluders
 * than slots.
 *
 * @param {FadeVolume[]} volumes
 * @param {Vec3Like} from @param {Vec3Like} to
 * @param {Array<{id: string|number, t: number}>} out Cleared and refilled.
 * @param {number} maxDistance 0 = unlimited.
 * @returns {Array<{id: string|number, t: number}>} `out`
 */
export function cast(volumes, from, to, out = [], maxDistance = 0) {
  out.length = 0;

  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  const len = Math.hypot(dx, dy, dz);
  if (!(len > 1e-4)) return out;

  // Camera-relative basis for the endpoint offsets: `right` is the horizontal
  // perpendicular of the view ray, `up` is world up. Good enough for a rig that
  // never rolls, and this project's does not.
  let rx = -dz;
  let rz = dx;
  const rl = Math.hypot(rx, rz) || 1;
  rx /= rl;
  rz /= rl;

  const limit = maxDistance > 0 ? maxDistance / len : 1;

  for (const v of volumes) {
    let best = Infinity;
    for (const [ro, uo] of RAY_OFFSETS) {
      const tx = to.x + rx * ro;
      const ty = to.y + uo;
      const tz = to.z + rz * ro;
      const t = hitVolume(v, from.x, from.y, from.z, tx - from.x, ty - from.y, tz - from.z);
      if (t >= 0 && t < best) best = t;
    }
    if (best < Infinity && best <= limit) out.push({ id: v.id, t: best });
  }

  out.sort((a, b) => a.t - b.t);
  return out;
}

/**
 * Segment-vs-volume. Tests the whole box first (the cheap reject), then each
 * `part` if there are any.
 * @param {FadeVolume} v
 * @param {number} ox @param {number} oy @param {number} oz
 * @param {number} dx @param {number} dy @param {number} dz Full segment vector,
 *   so the parameter range is [0, 1].
 * @returns {number} Entry `t` in [0, 1], or -1 for a miss.
 */
function hitVolume(v, ox, oy, oz, dx, dy, dz) {
  const outer = slab(v, ox, oy, oz, dx, dy, dz);
  if (outer < 0 || !v.parts?.length) return outer;
  let best = -1;
  for (const p of v.parts) {
    const t = slab(p, ox, oy, oz, dx, dy, dz);
    if (t >= 0 && (best < 0 || t < best)) best = t;
  }
  return best;
}

/**
 * The slab test, clipped to the segment. A ray STARTING inside the box returns
 * 0 — which is right: the camera is inside a building and everything it can see
 * of that building is in the way.
 * @param {{minX: number, minY: number, minZ: number, maxX: number, maxY: number, maxZ: number}} b
 * @param {number} ox @param {number} oy @param {number} oz
 * @param {number} dx @param {number} dy @param {number} dz
 * @returns {number} Entry `t`, or -1.
 */
function slab(b, ox, oy, oz, dx, dy, dz) {
  let t0 = 0;
  let t1 = 1;

  // x
  if (Math.abs(dx) < 1e-9) {
    if (ox < b.minX || ox > b.maxX) return -1;
  } else {
    const i = 1 / dx;
    let a = (b.minX - ox) * i;
    let c = (b.maxX - ox) * i;
    if (a > c) {
      const s = a;
      a = c;
      c = s;
    }
    if (a > t0) t0 = a;
    if (c < t1) t1 = c;
    if (t0 > t1) return -1;
  }
  // y
  if (Math.abs(dy) < 1e-9) {
    if (oy < b.minY || oy > b.maxY) return -1;
  } else {
    const i = 1 / dy;
    let a = (b.minY - oy) * i;
    let c = (b.maxY - oy) * i;
    if (a > c) {
      const s = a;
      a = c;
      c = s;
    }
    if (a > t0) t0 = a;
    if (c < t1) t1 = c;
    if (t0 > t1) return -1;
  }
  // z
  if (Math.abs(dz) < 1e-9) {
    if (oz < b.minZ || oz > b.maxZ) return -1;
  } else {
    const i = 1 / dz;
    let a = (b.minZ - oz) * i;
    let c = (b.maxZ - oz) * i;
    if (a > c) {
      const s = a;
      a = c;
      c = s;
    }
    if (a > t0) t0 = a;
    if (c < t1) t1 = c;
    if (t0 > t1) return -1;
  }
  return t0;
}

/**
 * Reconciles the hit list with the slots.
 *
 * Slots are STICKY: a volume that keeps being hit keeps its slot and its ramp.
 * A volume that stops being hit has `want` dropped to 0 and holds the slot
 * until it has faded out (`update` releases it) — otherwise a building would
 * snap back to solid the instant a closer one wanted its uniform.
 *
 * @param {FadeSlot[]} slots
 * @param {Array<{id: string|number, t: number}>} hits Nearest first.
 * @param {Map<string|number, FadeVolume>} byId
 */
function assign(slots, hits, byId) {
  for (const s of slots) s.want = 0;

  for (const h of hits) {
    const s = slots.find((x) => x.id === h.id);
    if (s) s.want = 1;
  }

  for (const h of hits) {
    if (slots.some((x) => x.id === h.id)) continue;
    // Free slots first; failing that, steal from something already on its way
    // out (`want` 0 and the faintest), which is the least visible swap.
    let free = slots.find((x) => x.id === null);
    if (!free) {
      let worst = null;
      for (const x of slots) {
        if (x.want !== 0) continue;
        if (!worst || x.amount < worst.amount) worst = x;
      }
      free = worst ?? undefined;
    }
    if (!free) break; // every slot is busy with something still occluding
    const v = byId.get(h.id);
    if (!v) continue;
    free.id = h.id;
    free.want = 1;
    free.amount = 0;
    const cx = (v.minX + v.maxX) * 0.5;
    const cy = (v.minY + v.maxY) * 0.5;
    const cz = (v.minZ + v.maxZ) * 0.5;
    free.center.value.set(cx, cy, cz, 0);
    free.half.value.set(
      Math.max(1e-3, (v.maxX - v.minX) * 0.5),
      Math.max(1e-3, (v.maxY - v.minY) * 0.5),
      Math.max(1e-3, (v.maxZ - v.minZ) * 0.5),
      free.half.value.w,
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Ordered dither
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 2x2 Bayer, as arithmetic: `fract(x/2 + y*y*3/4)` walks the integer lattice
 * through {0, 1/2, 3/4, 1/4} — the classic [[0,2],[3,1]]/4 matrix, with no
 * texture and no branch.
 * @param {TSLNode} a vec2
 * @returns {TSLNode} float in {0, .25, .5, .75}
 */
function bayer2(a) {
  const f = a.floor();
  return f.x.mul(0.5).add(f.y.mul(f.y).mul(0.75)).fract();
}

/**
 * 4x4 Bayer in [0, 0.9375], 16 levels — the standard recursive expansion of
 * {@link bayer2}. Fed SCREEN pixel coordinates, so the pattern is pinned to the
 * display and does not crawl across a surface as the camera moves.
 * @param {TSLNode} a vec2 (pixels)
 * @returns {TSLNode} float
 */
export function bayer4(a) {
  return bayer2(a.mul(0.5)).mul(0.25).add(bayer2(a));
}
